import React, { useState } from 'react';
import {
  i18nNamespace,
  DepositWithdrawalVariant,
} from 'modules/2023-q3/deposit-withdrawal/interface/deposit-withdrawal';
import { useTranslation } from 'app/i18n';
import { Box } from '@material-ui/core';
import { Typography, TypographyVariant } from 'components/core';
import { FormContainer } from 'modules/2023-q3/components/forms/components/form-container/form-container';
import { Button } from 'modules/2023-q3/components/button/button';
import { MobileView } from 'modules/2023-q3/components/mobile-view/mobile-view';
import {
  ButtonContent,
  SupportedIcons,
} from 'modules/2023-q3/components/button/button-content/button-content';
import {
  IInputAmount,
  InputAmount,
} from 'modules/2023-q3/deposit-withdrawal/pages/form/components/input-amount/input-amount';
import {
  ISelectACHBank,
  SelectSwitchACHBank,
} from 'modules/2023-q3/deposit-withdrawal/pages/form/components/select-switch-ach-bank/select-switch-ach-bank';
import { BankAccount } from 'types';
import { OverWithdrawalLimitMessage } from 'modules/2023-q3/deposit-withdrawal/withdrawal/components/over-withdrawal-limit-message/over-withdrawal-limit-message';
import { useForm } from 'react-hook-form';
import { WireInstructionsLink } from 'modules/2024-q4/apex-ach/deposit-apex-ach/pages/wire-instructions/wire-instructions-link';
import { FeatureFlag, FeatureFlags } from 'utils/feature-flags';
import {
  ISelectFrequency,
  SelectFrequency,
} from 'modules/2024-q1/schedule-transfer/deposit-withdrawal/pages/form/components/select-frequency/select-frequency';
import { ListScheduleTransfers } from 'modules/2024-q1/schedule-transfer/list-schedule-transfers/list-schedule-transfers';
import {
  IInputDate,
  InputDate,
} from 'modules/2024-q1/schedule-transfer/deposit-withdrawal/pages/form/components/input-date/input-date';
import { Styled } from 'modules/2023-q3/deposit-withdrawal/pages/form/form.style';
import { dayjs } from 'app/dayjs';
import isToday from 'dayjs/plugin/isToday';
import { ManualWireFormAccordion } from 'modules/2025-q1/manual-wire-form-accordion/manual-wire-form-accordion';
dayjs.extend(isToday);

interface FormProps
  extends IInputAmount,
    ISelectACHBank,
    ISelectFrequency,
    IInputDate {
  isOverMaxAmount?: boolean;

  nextStep: () => void;

  overMaxAmountMessage?: string;

  previousStep?: () => void;
}

export const Form: React.FC<FormProps> = ({
  amount,
  bank,
  frequency,
  isOverMaxAmount,
  nextStep,
  overMaxAmountMessage,
  previousStep,
  setAmount,
  setBank,
  setFrequency,
  setStartDate,
  startDate,
  variant,
}) => {
  const { t } = useTranslation(i18nNamespace);

  const { handleSubmit } = useForm();

  const [isSelectingBank, setIsSelectingBank] = useState(false);
  const [isSelectingFrequency, setIsSelectingFrequency] = useState(false);

  const isDeposit = variant === DepositWithdrawalVariant.Deposit;

  const isScheduled = !dayjs(startDate).isToday();

  const canContinue =
    amount &&
    amount > 0.0 &&
    bank !== undefined &&
    !(isOverMaxAmount && !isScheduled);

  const handleContinue = () => {
    if (canContinue) {
      nextStep();
    }
  };

  const onSubmit = async () => {
    if (canContinue) {
      handleContinue();
    }
  };

  const ContinueButton = ({ mobile }: { mobile?: boolean }) => {
    return (
      <Box mt={mobile ? 10 : undefined}>
        <Button
          borderRadius={mobile ? 0 : undefined}
          bottomFixed={mobile}
          disabled={!canContinue}
          fullWidth
          onClick={handleContinue}
        >
          <ButtonContent iconEnd={SupportedIcons.ArrowRight} mobile={mobile}>
            {t('Continue', { ns: 'app' })}
          </ButtonContent>
        </Button>
      </Box>
    );
  };

  return (
    <>
      <MobileView>
        <ManualWireFormAccordion />

        <ListScheduleTransfers variant={variant} />
      </MobileView>

      <FormContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box mb={3}>
            <Typography color="black19" variant={TypographyVariant.Header3}>
              {isDeposit
                ? t('Deposit to Treasure')
                : t('Withdraw from Treasure')}
            </Typography>
          </Box>

          <MobileView
            mobile={
              <>
                <Box mb={3}>
                  <ManualWireFormAccordion />
                </Box>

                <Box mb={3}>
                  <ListScheduleTransfers variant={variant} />
                </Box>
              </>
            }
          ></MobileView>

          <Styled.FormBlocks disableLastRoundedCorners={isSelectingBank}>
            <Styled.FormBlock px={{ xs: 0, sm: 2.5 }} py={2.5}>
              <InputAmount
                amount={amount}
                isOverMaxAmount={isOverMaxAmount}
                setAmount={setAmount}
                variant={variant}
              />
            </Styled.FormBlock>

            <Styled.FormBlock px={{ xs: 0, sm: 2.5 }} py={2.5}>
              <SelectSwitchACHBank
                bank={bank}
                isSelectingBank={isSelectingBank}
                setBank={setBank}
                setIsSelectingBank={setIsSelectingBank}
                variant={variant}
              />
            </Styled.FormBlock>

            <FeatureFlag
              enabled={
                <Styled.FormBlock px={{ xs: 0, sm: 2.5 }} py={2.5}>
                  <SelectFrequency
                    frequency={frequency}
                    isSelecting={isSelectingFrequency}
                    setFrequency={setFrequency}
                    setIsSelecting={setIsSelectingFrequency}
                  />
                </Styled.FormBlock>
              }
              flag={FeatureFlags.REACT_APP_2024_Q1_SCHEDULED_TRANSFERS}
            />

            <FeatureFlag
              enabled={
                <Styled.FormBlock px={{ xs: 0, sm: 2.5 }} py={2.5}>
                  <InputDate
                    setStartDate={setStartDate}
                    startDate={startDate}
                    variant={variant}
                  />
                </Styled.FormBlock>
              }
              flag={FeatureFlags.REACT_APP_2024_Q1_SCHEDULED_TRANSFERS}
            />
          </Styled.FormBlocks>

          {!isDeposit && isOverMaxAmount && overMaxAmountMessage && (
            <MobileView
              mobile={
                <OverWithdrawalLimitMessage
                  overMaxAmountMessage={overMaxAmountMessage}
                />
              }
            >
              {!isSelectingBank && (
                <OverWithdrawalLimitMessage
                  overMaxAmountMessage={overMaxAmountMessage}
                />
              )}
            </MobileView>
          )}

          {isDeposit && previousStep && (
            <MobileView
              mobile={
                <Box mt={4} mb={15} textAlign="center">
                  <WireInstructionsLink onClick={previousStep} />
                </Box>
              }
            ></MobileView>
          )}

          <MobileView mobile={<ContinueButton mobile />}>
            <Box mt={2}>
              <ContinueButton />
            </Box>
          </MobileView>
        </form>
      </FormContainer>

      {isDeposit && previousStep && (
        <MobileView>
          <Box mt={4} textAlign="center">
            <WireInstructionsLink onClick={previousStep} />
          </Box>
        </MobileView>
      )}
    </>
  );
};
